import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService, { Site } from '../services/apiService';
import UpgradePrompt from '../components/UpgradePrompt';
import ShareSuccess from '../components/ShareSuccess';
import QuickStart from '../components/QuickStart';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Globe, Plus, Settings, Languages, FileText, Loader2, X } from 'lucide-react';

const Sites: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSite, setNewSite] = useState({
    domain: '',
    name: '',
    default_language: 'en'
  });
  const [creating, setCreating] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState<any>(null);
  const [showShareSuccess, setShowShareSuccess] = useState<any>(null);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSites();
      setSites(response.sites);

      if (response.sites.length === 0) {
        setIsFirstTime(true);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSite = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setCreating(true);
      const response = await apiService.createSite(newSite);
      setShowCreateModal(false);
      setNewSite({ domain: '', name: '', default_language: 'en' });
      await loadSites();

      if (response) {
        setShowShareSuccess({
          siteUrl: newSite.domain,
          languages: ['Spanish', 'French']
        });
      }
    } catch (error: any) {
      if (error.status === 402) {
        setShowUpgradePrompt({
          type: 'modal',
          title: 'Site Limit Reached',
          message: error.message || 'You\'ve reached your site limit for this plan.',
          feature: 'sites'
        });
      } else {
        setError(error.message);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleQuickStartComplete = (result: string) => {
    setShowQuickStart(false);
    if (result === 'success') {
      loadSites();
    }
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center mb-8">
          <div className="sm:flex-auto">
            <Skeleton className="h-7 w-24 mb-2" />
            <Skeleton className="h-5 w-64" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-2 w-full mb-4" />
                <div className="flex gap-2">
                  <Skeleton className="h-9 flex-1" />
                  <Skeleton className="h-9 flex-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center mb-8">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-foreground">Sites</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage your localized websites and their configurations.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Site
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-destructive/10 border border-destructive/20 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Sites Grid */}
      {sites.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Globe className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No sites yet</h3>
            <p className="text-muted-foreground mb-6">Get started by creating your first localized website.</p>
            <div className="flex justify-center gap-3">
              <Button onClick={() => setShowQuickStart(true)}>
                Quick Start Guide
              </Button>
              <Button variant="outline" onClick={() => setShowCreateModal(true)}>
                Create manually
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {sites.map((site) => (
            <Card key={site.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Globe className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{site.name}</CardTitle>
                    <CardDescription className="truncate">{site.domain}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Translation Progress</span>
                    <span className="font-medium">{site.translated_percentage}%</span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${site.translated_percentage}%` }}
                    />
                  </div>
                </div>

                <div className="flex justify-between text-sm">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <FileText className="h-4 w-4" />
                    <span><strong className="text-foreground">{site.total_keys}</strong> keys</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Languages className="h-4 w-4" />
                    <span><strong className="text-foreground">{site.enabled_languages.length}</strong> languages</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button asChild className="flex-1">
                    <Link to={`/sites/${site.id}/translations`}>
                      Translations
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="icon">
                    <Link to={`/sites/${site.id}/settings`}>
                      <Settings className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Site Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Add New Site</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowCreateModal(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Enter the details for your new localized website.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateSite} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    required
                    value={newSite.domain}
                    onChange={(e) => setNewSite({ ...newSite, domain: e.target.value })}
                    placeholder="example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Site Name</Label>
                  <Input
                    id="name"
                    required
                    value={newSite.name}
                    onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                    placeholder="My Website"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="language">Default Language</Label>
                  <select
                    id="language"
                    value={newSite.default_language}
                    onChange={(e) => setNewSite({ ...newSite, default_language: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating}>
                    {creating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Site'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Start Modal */}
      {showQuickStart && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <QuickStart
            onComplete={handleQuickStartComplete}
            onSkip={() => setShowQuickStart(false)}
          />
        </div>
      )}

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <UpgradePrompt
          {...showUpgradePrompt}
          onClose={() => setShowUpgradePrompt(null)}
        />
      )}

      {/* Share Success Modal */}
      {showShareSuccess && (
        <ShareSuccess
          {...showShareSuccess}
          onClose={() => setShowShareSuccess(null)}
        />
      )}
    </div>
  );
};

export default Sites;
